import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { api } from '../api/client';

const TOKEN_KEY = 'picklecourts_data_token';
const USER_KEY = 'picklecourts_data_user';

interface User {
  id: string;
  email: string;
  role: string;
  venueId: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(readStoredUser);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.login(email, password);
    localStorage.setItem(TOKEN_KEY, data.token);
    const userData: User = {
      id: data.admin.id,
      email: data.admin.email,
      role: data.admin.role,
      venueId: data.admin.venueId,
    };
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const isAuthenticated = !!user;
  const isSuperAdmin = user?.role?.toUpperCase() === 'SUPERADMIN';

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isSuperAdmin, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
